<script setup>
import { computed, ref, watch } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import { useAlert } from 'dashboard/composables';
import DrivesAPI from 'dashboard/api/drives';
import MicrosoftFilePicker from './MicrosoftFilePicker.vue';

const props = defineProps({
  driveId: {
    type: Number,
    default: null,
  },
  filterType: {
    type: String,
    default: null,
  },
  viewerMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'cancel']);
const show = defineModel('show', { type: Boolean, default: false });
const provider = ref(null);
const loadError = ref(null);
const isLoading = ref(false);
const googlePickerOpened = ref(false);

async function loadProvider() {
  if (!props.driveId) {
    loadError.value = 'Missing drive';
    provider.value = null;
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  loadError.value = null;
  provider.value = null;
  try {
    const res = await DrivesAPI.getPickerConfig(props.driveId);
    const data = res.data;
    if (!data?.success || !data?.provider) {
      loadError.value = data?.error || 'Failed to load picker';
      isLoading.value = false;
      return;
    }
    if (!['google', 'onedrive', 'sharepoint'].includes(data.provider)) {
      loadError.value = 'Picker not available for this drive';
      isLoading.value = false;
      return;
    }
    provider.value = data.provider;
  } catch (err) {
    loadError.value = err?.message || 'Failed to load picker';
  } finally {
    isLoading.value = false;
  }
}

async function openGooglePicker() {
  if (!props.driveId || provider.value !== 'google') return;
  try {
    const { useGooglePicker } = await import(
      'dashboard/composables/useGooglePicker'
    );
    const picker = useGooglePicker();
    googlePickerOpened.value = true;
    await picker.buildAndShowPickerInPlace({
      driveId: props.driveId,
      filterType: props.filterType,
      onSelect: files => {
        const f = Array.isArray(files) ? files[0] : files;
        if (f && !props.viewerMode) {
          emit('select', { ...f, drive_id: props.driveId, provider: 'google' });
        }
        show.value = false;
      },
      onCancel: () => {
        show.value = false;
        emit('cancel');
      },
    });
  } catch (err) {
    useAlert(err?.message || 'Failed to open Google Picker');
    show.value = false;
  } finally {
    googlePickerOpened.value = false;
  }
}

watch(
  () => [show.value, props.driveId],
  ([isShow, id]) => {
    if (isShow && id) {
      googlePickerOpened.value = false;
      loadProvider();
    }
  },
  { immediate: true }
);

watch(show, isShow => {
  if (!isShow) googlePickerOpened.value = false;
});

watch(
  () => [provider.value, isLoading.value],
  ([p, loading]) => {
    if (
      p === 'google' &&
      !loading &&
      show.value &&
      props.driveId &&
      !googlePickerOpened.value
    ) {
      openGooglePicker();
    }
  }
);

function normalizePayload(fileOrFiles) {
  let payload =
    Array.isArray(fileOrFiles) && fileOrFiles.length === 1
      ? fileOrFiles[0]
      : fileOrFiles;
  if (Array.isArray(payload)) {
    payload = payload.map(f =>
      f && typeof f === 'object'
        ? {
            ...f,
            drive_id: props.driveId,
            provider: f.provider || provider.value,
          }
        : f
    );
  } else if (payload && typeof payload === 'object') {
    payload = {
      ...payload,
      drive_id: props.driveId,
      provider: payload.provider || provider.value,
    };
  }
  return payload;
}

function handleSelect(fileOrFiles) {
  if (props.viewerMode) {
    show.value = false;
    return;
  }
  emit('select', normalizePayload(fileOrFiles));
  show.value = false;
}

function handleCancel() {
  emit('cancel');
  show.value = false;
}

const isGoogle = computed(() => provider.value === 'google');
const isMicrosoft = computed(
  () => provider.value === 'onedrive' || provider.value === 'sharepoint'
);
</script>

<template>
  <Teleport to="body">
    <Modal
      v-model:show="show"
      size="large"
      :show-close-button="false"
      @close="handleCancel"
    >
      <div class="flex flex-col min-h-[480px] max-h-[85vh]">
        <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div
            v-if="isLoading"
            class="flex-1 flex items-center justify-center py-12 text-slate-600 dark:text-slate-400"
          >
            <span class="text-sm">Loading...</span>
          </div>
          <div
            v-else-if="loadError"
            class="flex-1 flex flex-col items-center justify-center p-6 text-center"
          >
            <p class="text-sm text-red-600 dark:text-red-400 mb-4">
              {{ loadError }}
            </p>
            <button
              type="button"
              class="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
              @click="handleCancel"
            >
              Close
            </button>
          </div>
          <div
            v-else-if="isGoogle"
            class="flex-1 flex flex-col items-center justify-center py-12 px-6 text-slate-600 dark:text-slate-400 text-center"
          >
            <span class="text-sm">
              {{
                googlePickerOpened
                  ? 'Use the Google Picker above to select a file.'
                  : 'Opening Google Picker...'
              }}
            </span>
          </div>
          <MicrosoftFilePicker
            v-else-if="isMicrosoft && driveId"
            :drive-id="driveId"
            :filter-type="filterType"
            :viewer-mode="viewerMode"
            class="flex-1 min-h-[500px] w-full overflow-hidden"
            @select="handleSelect"
            @cancel="handleCancel"
          />
        </div>
      </div>
    </Modal>
  </Teleport>
</template>
